import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ProgressBar } from '../ui/ProgressBar';
import { getLaborReport, calculateWeeklyRP } from '../../game/systems/labor';
import { staggerContainer, staggerChild } from '../../ui/animations';

const BASE = process.env.PUBLIC_URL + '/assets/';

function workerSprite(type) {
  if (type === 'manager') return BASE + 'worker-owner.svg';
  return BASE + 'worker-master-grower.svg';
}

export function WorkforceScreen({ state, dispatch }) {
  const [pendingHire, setPendingHire] = useState(null); // candidate to hire
  const [assignRoomId, setAssignRoomId] = useState('');  // room to assign after hire

  function handleHireConfirm() {
    if (!pendingHire) return;
    dispatch({ type: 'HIRE_WORKER', payload: { workerData: pendingHire } });
    if (assignRoomId) {
      // Dispatch after a tick so the worker exists in state first
      setTimeout(() => {
        dispatch({ type: 'ASSIGN_WORKER', payload: { workerId: pendingHire.id, roomId: assignRoomId } });
      }, 50);
    }
    setPendingHire(null);
    setAssignRoomId('');
  }

  const laborReports = getLaborReport(state.rooms, state.workers, state.unlockedTech);
  const weeklyWages = state.workers.reduce((s, w) => s + w.currentWage, 0);

  const totalRequired = laborReports.reduce((s, r) => s + r.required, 0);
  const totalProduced = laborReports.reduce((s, r) => s + r.produced, 0);
  const coverageRatio = totalRequired > 0 ? Math.min(1, totalProduced / totalRequired) : 1;

  return (
    <>
    <div className="screen" style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 14 }}>

      {/* LEFT — Current staff */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Labor coverage */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="panel-title">Labor Coverage</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {state.workers.length} workers · ${weeklyWages.toLocaleString()}/wk wages
            </span>
          </div>
          <ProgressBar value={coverageRatio * 100} showLabel label={`Facility coverage — ${Math.round(coverageRatio * 100)}%`} size="lg" />
          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {laborReports.map(report => {
              const room = state.rooms.find(r => r.id === report.roomId);
              if (!room || !room.unlocked) return null;
              const cov = Math.round(report.coverage * 100);
              return (
                <motion.div key={report.roomId} variants={staggerChild} style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '6px 8px', fontSize: 11 }}>
                  <div style={{ fontWeight: 600, marginBottom: 3 }}>{room.name}</div>
                  <ProgressBar value={cov} size="sm" />
                  <div style={{ marginTop: 3, color: cov < 60 ? 'var(--red)' : 'var(--text-muted)' }}>{cov}% staffed</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Research Lab panel — shown when any scientist is on staff */}
        {state.workers.some(w => w.type === 'scientist') && (
          <div className="panel">
            <div className="panel-title">🔬 Research Lab</div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              Scientists assigned here generate Research Points to unlock tech upgrades.
            </p>
            <div style={{ fontSize: 13 }}>
              <strong style={{ color: 'var(--green)' }}>{calculateWeeklyRP(state.workers)} RP/week</strong>
              {state.activeResearch ? (
                <span style={{ color: 'var(--text-muted)', marginLeft: 10, fontSize: 12 }}>
                  Active: {state.activeResearch.nodeId} — {state.activeResearch.pointsAccumulated}/{state.activeResearch.pointsRequired} RP
                </span>
              ) : (
                <span style={{ color: 'var(--text-dim)', marginLeft: 10, fontSize: 12 }}>
                  No active research — start one in the Research tab.
                </span>
              )}
            </div>
          </div>
        )}

        {/* Managers section — shown when any managers are on staff */}
        {state.workers.some(w => w.type === 'manager') && (
          <div className="panel">
            <div className="panel-title">🤖 Automated Rooms</div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              Managers assigned to a room via the Room Detail → Automation panel will auto-plant and auto-harvest.
            </p>
            {state.workers.filter(w => w.type === 'manager').map(mgr => {
              const managedRoom = state.rooms.find(r => r.automation?.managerId === mgr.id && r.automation?.enabled);
              return (
                <div key={mgr.id} style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{mgr.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>skill {mgr.skill} · ${mgr.currentWage}/wk</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {managedRoom ? (
                      <>
                        <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700 }}>● Automating</span>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{managedRoom.name}</div>
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Not automating</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Staff table */}
        <div className="panel">
          <div className="panel-title">Staff ({state.workers.length})</div>
          {state.workers.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <span className="empty-title">No Staff</span>
              <span className="empty-desc">Hire from the job market to the right.</span>
            </div>
          ) : (
            <table className="worker-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Skill</th>
                  <th>Morale</th>
                  <th>Wage/wk</th>
                  <th>Assigned</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {state.workers.map(worker => {
                  return (
                    <tr key={worker.id}>
                      <td>
                        <img src={workerSprite(worker.type)} alt={worker.type} className="worker-avatar" />
                      </td>
                      <td style={{ fontWeight: 600 }}>{worker.name}</td>
                      <td><span className={`type-badge ${worker.type}`}>{worker.type}</span></td>
                      <td>
                        <div style={{ width: 50 }}>
                          <div style={{ fontSize: 11, color: 'var(--text)' }}>{worker.skill}</div>
                          <ProgressBar value={worker.skill} size="sm" />
                        </div>
                      </td>
                      <td>
                        <div style={{ width: 50 }}>
                          <div style={{ fontSize: 11, color: worker.morale < 40 ? 'var(--red)' : 'var(--text)' }}>{worker.morale}</div>
                          <div className="morale-bar">
                            <div className="morale-fill" style={{ width: `${worker.morale}%`, background: worker.morale > 65 ? 'var(--green)' : worker.morale > 35 ? 'var(--yellow)' : 'var(--red)' }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ color: 'var(--yellow)' }}>${worker.currentWage.toLocaleString()}</td>
                      <td style={{ fontSize: 12 }}>
                        <select
                          value={worker.assignedRoomId || ''}
                          onChange={e => {
                            const roomId = e.target.value;
                            if (roomId) dispatch({ type: 'ASSIGN_WORKER', payload: { workerId: worker.id, roomId } });
                            else dispatch({ type: 'UNASSIGN_WORKER', payload: { workerId: worker.id } });
                          }}
                          style={{ background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '2px 4px', fontSize: 11 }}
                        >
                          <option value="">— Unassigned —</option>
                          {worker.type === 'scientist' && (
                            <option value="lab">🔬 Research Lab</option>
                          )}
                          {state.rooms.filter(r => r.unlocked).map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => dispatch({ type: 'FIRE_WORKER', payload: { workerId: worker.id } })}
                        >
                          Fire
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RIGHT — Job market */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="panel">
          <div className="panel-title">💼 Job Market</div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
            New candidates appear weekly. Hiring fee = 50% of weekly wage.
          </p>
          {(state.jobMarket || []).length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No candidates this week.</div>
          ) : (
            <motion.div variants={staggerContainer} initial="initial" animate="animate" style={{ display: 'flex', flexDirection: 'column' }}>
              {(state.jobMarket || []).map(candidate => {
                const hiringFee = Math.round(candidate.wageDemand * 0.5);
                return (
                  <motion.div key={candidate.id} variants={staggerChild} style={{ background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <img src={workerSprite(candidate.type)} alt="" className="worker-avatar" />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{candidate.name}</div>
                        <span className={`type-badge ${candidate.type}`}>{candidate.type}</span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11, marginBottom: 8 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Skill: <strong style={{ color: 'var(--text)' }}>{candidate.skill}</strong></span>
                      <span style={{ color: 'var(--text-muted)' }}>Speed: <strong style={{ color: 'var(--text)' }}>{candidate.speed}</strong></span>
                      <span style={{ color: 'var(--text-muted)' }}>Reliability: <strong style={{ color: 'var(--text)' }}>{candidate.reliability}</strong></span>
                      <span style={{ color: 'var(--yellow)' }}>Wage: ${candidate.wageDemand}/wk</span>
                    </div>
                    <motion.button
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      disabled={state.cash < hiringFee}
                      onClick={() => { setPendingHire(candidate); setAssignRoomId(''); }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Hire (${hiringFee} fee)
                    </motion.button>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>

        {/* Wage summary */}
        <div className="panel">
          <div className="panel-title">Weekly Payroll</div>
          <div className="stat-row">
            <span className="lbl">Total Staff</span>
            <span className="val">{state.workers.length}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Weekly Wages</span>
            <span className="val text-yellow">${weeklyWages.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Cash on Hand</span>
            <span className={`val ${state.cash < weeklyWages ? 'text-red' : 'text-green'}`}>
              ${state.cash.toLocaleString()}
            </span>
          </div>
          {state.cash < weeklyWages * 2 && (
            <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--red-dim)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red)', fontSize: 11, color: 'var(--red)' }}>
              ⚠ Low cash — can't cover payroll for 2 weeks!
            </div>
          )}
        </div>
      </div>
    </div>

    {/* ── Hire + Assign Modal ── */}
    {pendingHire && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div className="panel" style={{ width: 340, padding: '24px 20px' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Hire {pendingHire.name}?</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
            <span className={`type-badge ${pendingHire.type}`}>{pendingHire.type}</span>
            <span style={{ marginLeft: 8 }}>Skill {pendingHire.skill} · ${pendingHire.wageDemand}/wk</span>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Assign to room (optional):</div>
          <select
            value={assignRoomId}
            onChange={e => setAssignRoomId(e.target.value)}
            style={{ width: '100%', background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '6px 8px', fontSize: 12, marginBottom: 16 }}
          >
            <option value="">— Unassigned —</option>
            {pendingHire.type === 'scientist' && (
              <option value="room-lab">🔬 Research Lab</option>
            )}
            {state.rooms.filter(r => r.unlocked && r.type !== 'lab').map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleHireConfirm}>
              ✓ Hire (${Math.round(pendingHire.wageDemand * 0.5)} fee)
            </button>
            <button className="btn btn-secondary" onClick={() => { setPendingHire(null); setAssignRoomId(''); }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
